import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

import ArrowShortIcon from '@/fo/icons/base/icons/arrow-short.svg';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';

import { styles } from './Arrows.styles';

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollPrev();
    onButtonClick?.(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollNext();

    onButtonClick?.(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    onPrevButtonClick,
    onNextButtonClick,
    prevBtnDisabled,
    nextBtnDisabled
  };
};

export interface ArrowsProps {
  className?: string;
  arrowType?: 'arrow' | 'chevron' | 'arrow-short';
  onClick?: () => void;
  disabled?: boolean;
}

export function PrevButton({
  className,
  arrowType = 'arrow',
  disabled = false,
  ...props
}: ArrowsProps) {
  const slots = styles({});

  return (
    <UiKitButtonBase
      {...props}
      className={slots.arrow({
        className: ['-left-6 rotate-180', className]
      })}
      rounded="full"
      color="outlined"
      disabled={disabled}
    >
      {arrowType === 'arrow' && <ArrowIcon />}
      {arrowType === 'chevron' && <ChevronIcon />}
      {arrowType === 'arrow-short' && <ArrowShortIcon />}
    </UiKitButtonBase>
  );
}

export function NextButton({
  className,
  arrowType = 'arrow',
  disabled = false,
  ...props
}: ArrowsProps) {
  const slots = styles({});

  return (
    <UiKitButtonBase
      {...props}
      className={slots.arrow({
        className: ['-right-6', className]
      })}
      rounded="full"
      color="outlined"
      disabled={disabled}
    >
      {arrowType === 'arrow' && <ArrowIcon />}
      {arrowType === 'chevron' && <ChevronIcon />}
      {arrowType === 'arrow-short' && <ArrowShortIcon />}
    </UiKitButtonBase>
  );
}
